import { useState, useEffect } from 'react';
import Modal from '@/components/common/Modal';
import Input from '@/components/common/Input';
import TextArea from '@/components/common/TextArea';
import Button from '@/components/common/Button';
import { useUpdateSet } from '@/hooks/useSets';
import type { Set } from '@/types/set';

interface EditSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  set: Set;
}

/**
 * Modal for editing an existing Set's name and description.
 */
export default function EditSetModal({ isOpen, onClose, set }: EditSetModalProps) {
  const [name, setName] = useState(set.name);
  const [description, setDescription] = useState(set.description);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const updateSet = useUpdateSet();

  // Reset form when modal opens with new set data
  useEffect(() => {
    if (isOpen) {
      setName(set.name);
      setDescription(set.description);
      setErrors({});
      updateSet.reset();
    }
  }, [isOpen, set.name, set.description]);

  const validate = (): boolean => {
    const newErrors: { name?: string; description?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Name cannot exceed 100 characters';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.trim().length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await updateSet.mutateAsync({
        setId: set.set_id,
        data: {
          name: name.trim(),
          description: description.trim(),
        },
      });
      onClose();
    } catch {
      // Error is handled by the mutation's error state
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Set" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="edit-set-name"
            className="mb-1 block text-sm font-medium text-neutral-300"
          >
            Name
          </label>
          <Input
            id="edit-set-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Show Booking"
            maxLength={100}
            error={errors.name}
          />
        </div>

        <div>
          <label
            htmlFor="edit-set-description"
            className="mb-1 block text-sm font-medium text-neutral-300"
          >
            Description
          </label>
          <TextArea
            id="edit-set-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe what moves in this Set should accomplish..."
            rows={3}
            maxLength={500}
            error={errors.description}
          />
        </div>

        {updateSet.isError && (
          <p className="text-sm text-error-600">
            Failed to update Set. Please try again.
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={updateSet.isPending}
            disabled={updateSet.isPending}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
